import {
  ListMessagesRequest,
  MessageType,
} from '@wix/ambassador-innovation-widget-v1-message/types';

import { isNonNullable } from '@/utils';

import { MESSAGES_PAGE_SIZE } from './config';
import {
  Message,
  parseColorField,
  parseDatetimeField,
  parseNumberField,
  parseSelectionField,
  parseTextField,
  WidgetApi,
} from './model';

export const messagesQueryBuilder = (cursor?: string): ListMessagesRequest => {
  return {
    cursorPaging: { limit: MESSAGES_PAGE_SIZE, cursor },
  };
};

const ALLOWED_MESSAGE_TYPES = new Set([
  MessageType.QUESTION,
  MessageType.ANSWER,
  MessageType.NAVIGATION_ANSWER,
  MessageType.USER_ANSWER,
  MessageType.VISITOR_OPTION_SELECTED,

  MessageType.INTRO,
  MessageType.LEGAL,
  MessageType.OFFLINE,
  MessageType.SUGGESTED_QUESTION,

  MessageType.CONTACT_FORM,
  MessageType.CONTACT_FORM_SUBMITTED,
]);

export const SPECIAL_MESSAGE_TYPES = new Set([
  MessageType.INTRO,
  MessageType.LEGAL,
  MessageType.OFFLINE,
  MessageType.SUGGESTED_QUESTION,
  MessageType.CONTACT_FORM,
  MessageType.CONTACT_FORM_SUBMITTED,
]);

export function formatDate(date: Date | string | number) {
  if (typeof date === 'number') {
    return date;
  }
  if (date instanceof Date) {
    return date.getTime();
  }
  try {
    const d = new Date(date);
    return d.getTime();
  } catch (err) {
    console.error(`Failed to format date [${typeof date}] ${date} `);
    return 0;
  }
}

export const formatMessage = (
  m: WidgetApi.Message,
  replaceDate?: number,
): Message => {
  return {
    ...m,
    id: m.id!,
    createdDate: replaceDate ?? formatDate(m.createdDate ?? 0),
    body: {
      ...m.body,
      // Keep only payloads with type (filter out empty system payloads)
      additionalData: m.body?.additionalData?.filter((data) => data.type),
    },
    visitorOptionsFields:
      m.visitorOptionsFields
        ?.map(
          (field) =>
            parseTextField(field) ??
            parseNumberField(field) ??
            parseDatetimeField(field) ??
            parseSelectionField(field) ??
            parseColorField(field),
        )
        .filter(isNonNullable) ?? [],
  };
};

export function formatMessages(messages?: WidgetApi.Message[]): Message[] {
  return (messages ?? [])
    .filter((m) => Boolean(m.id))
    .map((m) => formatMessage(m));
}

export const isAllowedMessage = ({ messageType }: WidgetApi.Message): boolean =>
  Boolean(messageType) && ALLOWED_MESSAGE_TYPES.has(messageType!);

export const isSpecialMessage = ({
  messageType,
}: WidgetApi.Message | Message): boolean =>
  Boolean(messageType) && SPECIAL_MESSAGE_TYPES.has(messageType!);
